import { useState, useEffect } from 'react';
import {
  Card,
  Typography,
  Flex,
  Row,
  Col,
  Input,
  message,
  Select,
  Divider,
  Modal,
} from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { FindOrganizationDto } from '@evimo/types/api/organization/find-organization/find-organization.dto';
import { useOrganizationMutation } from '~/app/hooks/organization/useOrganizationMutation';
import { OrganizationService } from '~/app/services/organization/organization.service';
import { UpdateOrganizationRequestDto } from '@evimo/types/api/organization/update-organization/update-organization.request.dto';
import { UpdateOrganizationResponseDto } from '@evimo/types/api/organization/update-organization/update-organization.response.dto';
import { OrganizationType } from '@evimo/core/modules/organization/domain/OrganizationType';
import { Language } from '@evimo/types/enums/language';
import { ALLOWED_ORGANIZATION_TYPES } from '~/app/constants/organizationAllowedTypes.types';
import { OrganizationStatus } from '@evimo/types/enums/organization-status';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '~/app/components/common/Button';
import { OrganizationCsmsSection } from './OrganizationCsmsSection/OrganizationCsmsSection';
import {
  buildExternalCsmsConfigPayload,
  formStateFromConfig,
  getSecondaryEndpoints,
  isExternalCsmsProxyEnabled,
  csmsSaveConfirmation,
  validateCsmsBaseUrl,
  type ExternalCsmsFormState,
} from './organizationExternalCsms.utils';
import { readOrganizationClientError } from '~/app/services/organization/readOrganizationClientError';

type OrganizationOverviewCardProps = {
  organization: FindOrganizationDto;
};

export function OrganizationOverviewCard({
  organization,
}: OrganizationOverviewCardProps) {
  const { t } = useTranslation(['organization', 'common']);
  const [name, setName] = useState(organization.name ?? '');
  const [organizationType, setOrganizationType] = useState<OrganizationType>(
    organization.organizationType as OrganizationType,
  );
  const [mainLanguage, setMainLanguage] = useState<Language>(
    organization.mainLanguage as Language,
  );
  const [status, setStatus] = useState<OrganizationStatus>(
    organization.active
      ? OrganizationStatus.ACTIVE
      : OrganizationStatus.INACTIVE,
  );
  const [parentId, setParentId] = useState<string | null>(
    organization.parentId ?? null,
  );

  const [csms, setCsms] = useState<ExternalCsmsFormState>(() =>
    formStateFromConfig(organization.externalCsmsConfig),
  );
  const [csmsError, setCsmsError] = useState<string | null>(null);

  useEffect(() => {
    setName(organization.name ?? '');
    setOrganizationType(organization.organizationType as OrganizationType);
    setMainLanguage(organization.mainLanguage as Language);
    setStatus(
      organization.active
        ? OrganizationStatus.ACTIVE
        : OrganizationStatus.INACTIVE,
    );
    setParentId(organization.parentId ?? null);
    setCsms(formStateFromConfig(organization.externalCsmsConfig));
    setCsmsError(null);
  }, [organization]);

  const { data: organizations = [] } = useQuery({
    queryKey: ['organizations'],
    queryFn: () => OrganizationService.findAllOrganizations(),
  });

  const queryClient = useQueryClient();

  const updateOrganizationMutation = useOrganizationMutation<
    UpdateOrganizationRequestDto & { orgId: string },
    UpdateOrganizationResponseDto
  >(({ orgId, ...dto }) => OrganizationService.updateOrganization(orgId, dto), {
    queryKey: ['organizations'],
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['organizations'],
      });
      queryClient.invalidateQueries({
        queryKey: ['organization', organization.id],
      });
      queryClient.invalidateQueries({
        queryKey: ['organization'],
      });
      queryClient.invalidateQueries({
        queryKey: ['assets', 'detail'],
      });
      queryClient.invalidateQueries({
        queryKey: ['assets', 'ocpp-settings'],
      });
      message.success(t('detail.updateSuccess', 'Organization updated!'));
    },
    onError: (error) => {
      void readOrganizationClientError(error).then((apiError) => {
        if (apiError) {
          setCsmsError(apiError);
          return;
        }
        message.error(t('detail.updateError', 'Update failed!'));
      });
    },
  });

  const persist = () => {
    if (csms.enableCsms && !validateCsmsBaseUrl(csms.csmsBaseUrl)) {
      setCsmsError(t('dialog.csmsUrlInvalid'));
      return;
    }
    const invalidSecondary = (csms.secondaries ?? []).some(
      (endpoint) => !validateCsmsBaseUrl(endpoint.baseUrl),
    );
    if (csms.enableCsms && invalidSecondary) {
      setCsmsError(t('dialog.csmsUrlInvalid'));
      return;
    }
    setCsmsError(null);
    updateOrganizationMutation.mutate({
      orgId: organization.id,
      name,
      organizationType,
      mainLanguage,
      parentId: parentId ?? null,
      active: status === OrganizationStatus.ACTIVE,
      externalCsmsConfig: buildExternalCsmsConfigPayload(
        csms,
        organization.externalCsmsConfig,
      ),
    });
  };

  const handleSave = () => {
    const confirmation = csmsSaveConfirmation(
      csms,
      organization.externalCsmsConfig,
    );
    const copy = {
      update: {
        title: t('dialog.csmsSaveTitle'),
        content: t('dialog.csmsSaveBody'),
      },
      clear: {
        title: t('dialog.csmsClearTitle'),
        content: t('dialog.csmsClearBody'),
      },
      primary: {
        title: t('dialog.csmsPrimaryChangeTitle'),
        content: t('dialog.csmsPrimaryChangeBody'),
      },
      removeSecondary: {
        title: t('dialog.csmsRemoveSecondaryTitle'),
        content: t('dialog.csmsRemoveSecondaryBody'),
      },
      disableSecondary: {
        title: t('dialog.csmsDisableSecondaryTitle'),
        content: t('dialog.csmsDisableSecondaryBody'),
      },
    }[confirmation ?? 'update'];
    Modal.confirm({
      title: copy.title,
      content: copy.content,
      okText: t('dialog.confirm'),
      cancelText: t('dialog.cancel'),
      onOk: persist,
    });
  };

  const handleRemoveSecondary = (index: number) => {
    setCsms({
      ...csms,
      secondaries: (csms.secondaries ?? []).filter(
        (_, endpointIndex) => endpointIndex !== index,
      ),
    });
  };

  return (
    <Card title={t('detail.orgDetails', 'Organization Details')}>
      <Typography.Text type="secondary" className="app-display-block app-mb-20">
        {t(
          'detail.orgSubDetails',
          "View and edit the organization's information.",
        )}
      </Typography.Text>
      <Flex vertical gap="middle" className="app-max-width-500">
        <Row align="middle">
          <Col span={9}>
            <Typography.Text strong>
              {t('detail.name', 'Organization Name')}
            </Typography.Text>
          </Col>
          <Col span={15}>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              style={{
                width: '100%',
                padding: 6,
                borderRadius: 5,
                border: '1px solid #d9d9d9',
              }}
            />
          </Col>
        </Row>
        <Row align="middle">
          <Col span={9}>
            <Typography.Text strong>
              {t('detail.type', 'Organization Type')}
            </Typography.Text>
          </Col>
          <Col span={15}>
            <Select
              value={organizationType}
              onChange={(value) => setOrganizationType(value)}
              style={{
                width: '100%',
                padding: 6,
                borderRadius: 5,
                border: '1px solid #d9d9d9',
                background: '#f5f5f5',
              }}
              options={ALLOWED_ORGANIZATION_TYPES.map((type) => ({
                value: type,
                label: t(`dialog.types.${type}`, type.replace(/_/g, ' ')),
              }))}
            />
          </Col>
        </Row>

        <Row align="middle">
          <Col span={9}>
            <Typography.Text strong>
              {t('detail.mainLanguage', 'Main Language')}
            </Typography.Text>
          </Col>
          <Col span={15}>
            <Select
              value={mainLanguage}
              onChange={(value) => setMainLanguage(value)}
              style={{
                width: '100%',
                padding: 6,
                borderRadius: 5,
                border: '1px solid #d9d9d9',
                background: '#f5f5f5',
              }}
              options={Object.values(Language).map((language) => ({
                value: language,
                label: t(`language.${language}`, language.replace('_', ' ')),
              }))}
            />
          </Col>
        </Row>

        <Row align="middle">
          <Col span={9}>
            <Typography.Text strong>
              {t('detail.status', 'Status')}
            </Typography.Text>
          </Col>
          <Col span={15}>
            <Select
              value={status}
              onChange={(value) => setStatus(value)}
              style={{
                width: '100%',
                padding: 6,
                borderRadius: 5,
                background: '#f5f5f5',
                border: '1px solid #d9d9d9',
              }}
              options={[
                {
                  value: OrganizationStatus.ACTIVE,
                  label: t('detail.status.ACTIVE', 'Active'),
                },
                {
                  value: OrganizationStatus.INACTIVE,
                  label: t('detail.status.INACTIVE', 'Inactive'),
                },
              ]}
            />
          </Col>
        </Row>

        <Row align="middle">
          <Col span={9}>
            <Typography.Text strong>
              {t('detail.parentOrg', 'Managed By')}
            </Typography.Text>
          </Col>
          <Col span={15}>
            <Select
              value={parentId ?? undefined}
              onChange={(value) => setParentId(value ?? null)}
              allowClear
              showSearch={{ optionFilterProp: 'label' }}
              style={{ width: '100%' }}
              options={organizations
                .filter((o) => o.id !== organization.id)
                .map((o) => ({ value: o.id, label: o.name }))}
            />
          </Col>
        </Row>

        <Divider titlePlacement="left" className="app-my-4">
          {t('detail.ocppSectionTitle')}
        </Divider>

        <OrganizationCsmsSection
          value={csms}
          primaryAlreadySaved={isExternalCsmsProxyEnabled(
            organization.externalCsmsConfig,
          )}
          savedSecondaryCount={
            getSecondaryEndpoints(organization.externalCsmsConfig).length
          }
          apiError={csmsError}
          onChange={setCsms}
          onRemoveSecondary={handleRemoveSecondary}
        />

        <Row justify="end">
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSave}
            loading={updateOrganizationMutation.isPending}
          >
            {t('detail.save', 'Save')}
          </Button>
        </Row>
      </Flex>
    </Card>
  );
}
